import { useEffect, useRef, useState, type MouseEvent as RMouseEvent, type ReactElement } from 'react';
import { call, iptv, rowOpened, titleApi, layoutApi, type EpgProgramme, type IptvChannel, seerrApi, type Brands, type CalEpisode, type CalMovie, type ExtItem, type MiniItem, type SeerrRequest, type SportEvent, type WidgetDef } from '../api';
import { useApp } from '../context';
import { useArtFrom, useExternalArt, useMeta, setFocusBackdrop } from '../meta';
import { displayTitle, isWatched, progress as plexProgress, subtitle, tileFlags, tmdbRef, type PlexClient, type PlexItem, type PlexLibrary } from '../plex';
import { linkProps, navigate } from '../router';
import { ChannelLogo, ChannelName, clock, playLink, SportTile } from './LiveBits';
import { RatingsRow, TitleLogo, TrailerModal } from './MetaBits';
import { TileFlags } from './MediaFlags';
import { useCardMenu, useHiddenCards } from './CardMenu';
import { LocalListButton } from './LocalListButton';
import { RequestCard } from './RequestCard';
import { PlayIcon } from './Icons';
import { RecommendButton } from './Recommend';
import { dayName, loadCalendar, RELEASE_LABEL, todayYmd } from '../calendar';
import { prefsNow, usePrefs } from '../prefs';
import { loadBif } from '../bif';
import { loadLeagues, loadWeek, statusText, summarize, type LeagueSummary } from '../sport';
import { addonsApi, progressApi, useAddons, type ProgressItem, type UpNext } from '../addons';
import { StreamList } from './AddonSources';
import { DECISION, tautulliApi, type TSession } from '../tautulli';
import { TileScore } from './MediaFlags';
import { SportWidget } from './SportWidgets';
import { usePolicy } from './TopNav';
import { InfoLine, NetflixRow, TrailerFrame, useBillboardItem } from './NetflixRow';
import { releaseOf, requestLabels, requestStatusOf, setRequestStatus, useRequestStatuses } from '../requests';

/**
 * Widgets: één bron (Plex, TMDB, MDBList, Trakt, Live TV, Seerr) in één weergave
 * (spotlight, poster, landscape, breed + logo, Top 10, wall).
 */

export interface DisplayItem {
  key: string;
  title: string;
  sub: string;
  overview?: string | null;
  poster?: string;
  backdrop?: string;
  /** Voor clearlogo en scores */
  ref: { type: 'movie' | 'tv'; tmdbId: number } | null;
  /** Staat in de bibliotheek: detailpagina en (bij films/afleveringen) direct afspelen */
  href?: string;
  playHref?: string;
  progress?: number;
  /** Niet in de bibliotheek: aanvragen */
  missing?: ExtItem;
  /** Voorbeeldplaatjes (BIF) en waar je was, voor een bewegend voorbeeld bij hover */
  bifUrl?: string | null;
  offsetSec?: number;
  /** Eigen label (Seerr-status, datum in de kalender) */
  badge?: string;
  badgeTone?: 'req' | 'own' | 'date';
  /** 4K, DV, Atmos… (alleen voor titels uit Plex) */
  flags?: { label: string; kind: 'res' | 'hdr' | 'audio' }[];
  /** Titel uit Plex: voor het kaartmenu (⋯ / rechtsklik / lang drukken) */
  plex?: PlexRef;
  /** Keuring uit Plex ("nl/16", "R", …) voor de leeftijdsgrens */
  rating?: string;
  /** Score 0-10 (Plex: publiek, anders critici) voor het scorelabel */
  score?: number;
}

/** Keuring naar leeftijd (Kijkwijzer, anders Amerikaans); onbekend = null. */
export function ratingAge(r: string | undefined): number | null {
  if (!r) return null;
  const v = r.split('/').pop()!.trim().toUpperCase();
  if (v === 'AL') return 0;
  if (/^\d{1,2}$/.test(v)) return Number(v);
  const map: Record<string, number> = { G: 0, PG: 9, 'PG-13': 13, R: 17, 'NC-17': 18, 'TV-Y': 0, 'TV-Y7': 7, 'TV-G': 0, 'TV-PG': 9, 'TV-14': 14, 'TV-MA': 17 };
  return map[v] ?? null;
}

export interface PlexRef {
  ratingKey: string;
  type: string;
  viewOffset?: number;
  duration?: number;
  showKey?: string;
  guid?: string;
  watched: boolean;
  /** Staat in Verder kijken */
  onDeck?: boolean;
  /** Addon-titel (geen Plex): voortgang staat bij tumedia */
  progressId?: string;
  href?: string;
  /** Uit Trakt (elders gekeken / volgende aflevering): weghalen = verbergen */
  upnext?: { type: 'movie' | 'tv'; tmdbId: number };
}

/** Verder kijken van Trakt als kaart: in Plex → de titel; anders de info met bronnen uit addons. */
function fromUpNext(u: UpNext): DisplayItem {
  const sub = u.type === 'tv' ? `S${u.season ?? '?'} E${u.episode ?? '?'}${u.epTitle ? ` · ${u.epTitle}` : ''}` : [u.year].filter(Boolean).join('');
  const base = {
    key: `un:${u.type}:${u.tmdbId}`,
    title: u.title,
    sub,
    poster: u.poster ?? undefined,
    backdrop: u.backdrop ?? u.poster ?? undefined,
    ref: { type: u.type, tmdbId: u.tmdbId },
    progress: u.progress,
    badge: u.why === 'next' ? 'Volgende' : 'Trakt',
    badgeTone: 'date' as const,
  };
  if (u.ratingKey) {
    return { ...base, href: `/item/${u.ratingKey}`, plex: { ratingKey: u.ratingKey, type: u.type === 'tv' ? 'episode' : 'movie', watched: false, onDeck: true, upnext: { type: u.type, tmdbId: u.tmdbId }, href: `/item/${u.ratingKey}` } };
  }
  return {
    ...base,
    plex: { ratingKey: base.key, type: u.type, watched: false, onDeck: true, upnext: { type: u.type, tmdbId: u.tmdbId } },
    missing: { type: u.type, tmdbId: u.tmdbId, title: u.title, year: u.year, overview: null, poster: u.poster, backdrop: u.backdrop, ratingKey: null, imdb: u.imdb, season: u.season, episode: u.episode, epTitle: u.epTitle },
  };
}

/** Welke kaarten een menu krijgen: titels uit Plex, half gekeken addon-titels en Verder kijken van Trakt. */
export function menuRef(it: DisplayItem): PlexRef | undefined {
  return it.plex && (!it.missing || it.plex.upnext || it.plex.progressId) ? it.plex : undefined;
}

/** Half gekeken addon-titel als kaart in Verder kijken. */
function fromProgress(p: ProgressItem): DisplayItem {
  const href = p.addonId ? `/addon/${p.addonId}/${p.type}/${encodeURIComponent(p.metaId)}${p.type === 'series' ? `?ep=${encodeURIComponent(p.id)}` : ''}` : undefined;
  return {
    key: `ap:${p.id}`,
    title: p.name,
    sub: p.type === 'series' ? `S${p.season ?? '?'} E${p.episode ?? '?'}${p.epTitle ? ` · ${p.epTitle}` : ''}` : 'Film',
    poster: p.poster,
    backdrop: p.background ?? p.poster,
    ref: null,
    href,
    progress: p.duration ? Math.min(1, p.position / p.duration) : undefined,
    plex: { ratingKey: `ap:${p.id}`, type: p.type, viewOffset: p.position * 1000, duration: p.duration * 1000, watched: false, onDeck: true, progressId: p.id, href },
    // Niet uit een addon-catalogus: de info met bronnen openen
    ...(!href && p.tmdbId ? {
      ref: { type: p.type === 'series' ? 'tv' as const : 'movie' as const, tmdbId: p.tmdbId },
      missing: { type: p.type === 'series' ? 'tv' as const : 'movie' as const, tmdbId: p.tmdbId, title: p.name, year: null, overview: null, poster: p.poster ?? null, backdrop: p.background ?? null, ratingKey: null, imdb: p.metaId, season: p.season, episode: p.episode, epTitle: p.epTitle },
      badge: 'Verder',
      badgeTone: 'date' as const,
    } : {}),
  };
}

type Loaded =
  | { kind: 'items'; items: DisplayItem[]; heading?: string }
  | { kind: 'brands'; list: 'networks' | 'studios' | 'categories'; data: Brands }
  | { kind: 'sport'; events: SportEvent[] }
  | { kind: 'leagues'; list: LeagueSummary[] }
  | { kind: 'nowtv'; list: NowTv[] }
  | { kind: 'tautulli'; streams: TSession[] }
  | { kind: 'sportcenter' }
  | { kind: 'error'; message: string };

export function fromPlex(plex: PlexClient, i: PlexItem, onDeck = false): DisplayItem {
  const isEp = i.type === 'episode';
  return {
    key: `plex:${i.ratingKey}`,
    title: displayTitle(i),
    sub: isEp ? `S${i.parentIndex} E${i.index} · ${i.title}` : subtitle(i),
    overview: i.summary,
    poster: plex.image(isEp ? i.grandparentThumb ?? i.parentThumb : i.thumb, 300, 450),
    backdrop: plex.image(isEp ? i.thumb ?? i.grandparentArt : i.art ?? i.thumb, 960, 540),
    ref: tmdbRef(i),
    href: `/item/${i.ratingKey}`,
    playHref: i.type === 'movie' || isEp ? `/play/${i.ratingKey}${i.viewOffset ? `?offset=${i.viewOffset}` : ''}` : undefined,
    progress: plexProgress(i),
    bifUrl: i.type === 'movie' || isEp ? plex.bifUrl(i.Media?.[0]?.Part?.[0]) : null,
    offsetSec: (i.viewOffset ?? 0) / 1000,
    flags: tileFlags(i),
    rating: i.contentRating,
    score: i.audienceRating ?? i.rating,
    plex: {
      ratingKey: i.ratingKey,
      type: i.type,
      viewOffset: i.viewOffset,
      duration: i.duration,
      showKey: isEp ? i.grandparentRatingKey : undefined,
      guid: isEp ? i.grandparentGuid ?? undefined : i.guid,
      watched: isWatched(i),
      onDeck: onDeck || undefined,
    },
  };
}

/** Bij hover/focus na een korte pauze door de voorbeeldplaatjes bladeren, vanaf waar je was. */
function useHoverPreview(it: DisplayItem) {
  const [src, setSrc] = useState<string | null>(null);
  const timers = useRef<{ start?: number; tick?: number }>({});
  const stop = () => {
    window.clearTimeout(timers.current.start);
    window.clearInterval(timers.current.tick);
    setSrc(null);
  };
  const start = () => {
    if (!it.bifUrl || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    window.clearTimeout(timers.current.start);
    timers.current.start = window.setTimeout(() => {
      void loadBif(it.bifUrl!).then((b) => {
        if (!b) return;
        let t = Math.max(0, (it.offsetSec ?? 0) - 5);
        setSrc(b.frameAt(t));
        window.clearInterval(timers.current.tick);
        timers.current.tick = window.setInterval(() => {
          t += b.interval;
          setSrc(b.frameAt(t));
        }, 650);
      });
    }, 700);
  };
  useEffect(() => stop, []);
  return { src, start, stop };
}

export function fromExt(x: ExtItem): DisplayItem {
  if (!x.ratingKey && x.requestStatus) setRequestStatus(x.type, x.tmdbId, x.requestStatus, x.releaseDate ?? null);
  return {
    key: `${x.type}:${x.tmdbId}`,
    title: x.title,
    sub: [x.type === 'tv' ? 'Serie' : 'Film', x.year].filter(Boolean).join(' · '),
    overview: x.overview,
    poster: x.poster ?? undefined,
    backdrop: x.backdrop ?? undefined,
    ref: { type: x.type, tmdbId: x.tmdbId },
    href: x.ratingKey ? `/item/${x.ratingKey}` : undefined,
    playHref: x.ratingKey && x.type === 'movie' ? `/play/${x.ratingKey}` : undefined,
    missing: x.ratingKey ? undefined : x,
    flags: x.flags,
    score: x.score ?? undefined,
    ...(x.available === 'addon' ? { badge: 'Nu te kijken', badgeTone: 'own' as const } : {}),
  };
}

function fromRequest(r: SeerrRequest): DisplayItem | null {
  if (!r.media) return null;
  const label = r.approval === 'declined' ? 'Afgewezen' : r.approval === 'pending' ? 'Wacht op goedkeuring' : r.media.status === 'available' ? 'Beschikbaar' : r.media.status === 'partial' ? 'Deels beschikbaar' : 'Aangevraagd';
  return {
    key: `req:${r.id}`,
    title: r.media.title,
    sub: [r.media.year, label].filter(Boolean).join(' · '),
    poster: r.media.poster ?? undefined,
    backdrop: r.media.backdrop ?? undefined,
    ref: { type: r.media.mediaType, tmdbId: r.media.tmdbId },
    href: `/requests?q=${encodeURIComponent(r.media.title)}`,
    badge: label,
  };
}

function fromCalEpisode(e: CalEpisode): DisplayItem {
  const day = dayName(e.date);
  const have = !!e.ratingKey;
  return {
    key: `cal:${e.id}`,
    title: e.show.title,
    sub: `S${e.season} E${e.episode} · ${e.title}`,
    overview: e.overview,
    poster: e.show.poster ?? undefined,
    backdrop: e.still ?? e.show.backdrop ?? undefined,
    ref: { type: 'tv', tmdbId: e.show.tmdbId },
    href: e.ratingKey ? `/item/${e.ratingKey}` : e.show.ratingKey ? `/item/${e.show.ratingKey}` : undefined,
    playHref: e.ratingKey ? `/play/${e.ratingKey}` : undefined,
    missing: e.show.ratingKey ? undefined : { type: 'tv', tmdbId: e.show.tmdbId, title: e.show.title, year: null, overview: null, poster: e.show.poster, backdrop: e.show.backdrop, ratingKey: null },
    badge: have ? (e.date >= todayYmd(-1) ? 'Nieuw · nu te zien' : 'Nu te zien') : `${e.premiere ? 'Première · ' : ''}${day}`,
    badgeTone: have ? 'own' : 'date',
  };
}

function fromCalMovie(m: CalMovie): DisplayItem {
  return {
    key: `calm:${m.id}`,
    title: m.title,
    sub: `${RELEASE_LABEL[m.kind]} · ${dayName(m.date)}`,
    overview: m.overview,
    poster: m.poster ?? undefined,
    backdrop: m.backdrop ?? undefined,
    ref: { type: 'movie', tmdbId: m.tmdbId },
    href: m.ratingKey ? `/item/${m.ratingKey}` : `/requests?q=${encodeURIComponent(m.title)}`,
    playHref: m.ratingKey ? `/play/${m.ratingKey}` : undefined,
    badge: m.ratingKey ? 'In bibliotheek' : dayName(m.date),
    badgeTone: m.ratingKey ? 'own' : 'date',
  };
}

function defaultVideoLibrary(libs: PlexLibrary[]): string | undefined {
  return libs.find((l) => l.type === 'movie')?.key ?? libs.find((l) => l.type === 'show')?.key;
}

async function loadWidget(w: WidgetDef, plex: PlexClient, libs: PlexLibrary[]): Promise<Loaded> {
  const s = w.source;
  switch (s.kind) {
    case 'plex': {
      let items: PlexItem[] = [];
      if (s.list === 'watchlist') {
        const r = await layoutApi.items(s, w.limit, w.show, undefined, w.filter);
        return { kind: 'items', items: r.items.map(fromExt) };
      }
      if (s.list === 'collection') items = s.collection ? await plex.collectionItems(s.collection, w.filter ? Math.min(100, w.limit * 3) : w.limit) : [];
      else if (s.list === 'playlist') items = s.playlist ? await plex.playlistItems(s.playlist, w.limit) : [];
      else if (s.list === 'continueWatching') items = await plex.onDeck();
      else if (s.list === 'recentlyAdded') items = await plex.recentlyAdded(s.library);
      else if (s.list === 'shelf') {
        // Collector's Shelf: de server weet welke films 4K/DV/Atmos/Remux zijn (bibliotheek-index)
        const { keys } = await titleApi.shelf(s.shelf ?? 'dv', w.limit).catch(() => ({ keys: [] as string[] }));
        items = keys.length ? await plex.metadataMany(keys) : [];
      }
      else {
        const lib = s.library ?? defaultVideoLibrary(libs);
        if (!lib) return { kind: 'items', items: [] };
        const r = await plex.libraryItems(lib, {
          sort: 'addedAt:desc',
          unwatched: s.list === 'unwatched' ? true : undefined,
          genre: s.list === 'genre' ? s.genre : undefined,
          start: 0,
          size: w.filter ? Math.min(100, w.limit * 3) : w.limit,
        });
        items = r.items;
      }
      if (s.list === 'continueWatching') {
        // Ook wat je via addons keek, op volgorde van wanneer je het keek
        const [extra, trakt] = await Promise.all([
          progressApi.continue().then((r) => r.items).catch(() => [] as ProgressItem[]),
          progressApi.upnext().then((r) => r.items).catch(() => [] as UpNext[]),
        ]);
        // Trakt: niet dubbel met wat Plex al toont
        const plexKeys = new Set(items.map((i) => i.ratingKey));
        const plexShows = new Set(items.map((i) => (i.grandparentTitle ?? i.title).toLowerCase()));
        const traktOnly = trakt.filter((u) => !(u.ratingKey && plexKeys.has(u.ratingKey)) && !plexShows.has(u.title.toLowerCase()));
        const merged = [
          // Plex-volgorde aanhouden: zonder kijkmoment (volgende aflevering) net na de vorige
          ...items.map((i, n, all) => ({ at: (i.lastViewedAt ?? Math.min(...all.slice(0, n + 1).map((x) => x.lastViewedAt ?? Infinity)) - n) * 1000, d: fromPlex(plex, i, true) })),
          ...extra.map((p) => ({ at: p.updatedAt, d: fromProgress(p) })),
          ...traktOnly.map((u) => ({ at: u.at, d: fromUpNext(u) })),
        ].sort((a, b) => b.at - a.at);
        return { kind: 'items', items: merged.slice(0, w.limit).map((x) => x.d) };
      }
      // Filters van de rij: gezien verbergen, minimale score en schudden (zelfde volgorde de hele dag/week)
      const f = w.filter;
      if (f?.hideWatched) items = items.filter((i) => !(i.viewCount && i.viewCount > 0) && !(i.leafCount && i.viewedLeafCount === i.leafCount));
      if (f?.minScore) items = items.filter((i) => { const sc = i.audienceRating ?? i.rating; return sc == null || sc >= f.minScore!; });
      if (f?.shuffle) items = shuffleRow(items, f.shuffle, (i) => i.ratingKey);
      return { kind: 'items', items: items.slice(0, w.limit).map((i) => fromPlex(plex, i, false)) };
    }
    case 'live': {
      const acc = await iptv.accounts();
      if (!acc.accounts.some((a) => a.enabledForProfile)) return { kind: 'sport', events: [] };
      if (s.list === 'nowtv') return { kind: 'nowtv', list: await loadNowTv(w.limit, s.channels, s.categories) };
      if (s.list === 'leagues') {
        const [leagues, events] = await Promise.all([loadLeagues(), loadWeek()]);
        return { kind: 'leagues', list: summarize(leagues, events, s.leagues) };
      }
      const hours = s.hours ?? 12;
      const r = await iptv.sport(Math.max(24, hours));
      const now = Date.now();
      let events = r.events.filter((e) => e.live || e.start - now < hours * 3600_000);
      if (s.leagues?.length) events = events.filter((e) => !!e.league && s.leagues!.includes(e.league));
      if (s.sports?.length) events = events.filter((e) => s.sports!.includes(e.sport));
      if (s.nlOnly) {
        events = events
          .map((e) => ({ ...e, channels: e.channels.filter((c) => c.nl) }))
          .filter((e) => e.channels.length > 0);
      }
      return { kind: 'sport', events: events.slice(0, w.limit) };
    }
    case 'seerr': {
      const r = await seerrApi.requests();
      return { kind: 'items', items: r.requests.map(fromRequest).filter((x): x is DisplayItem => !!x).slice(0, w.limit) };
    }
    case 'calendar': {
      const cal = await loadCalendar();
      // Vanaf gisteren: wat net uit is en (nog) niet bekeken, plus wat eraan komt
      const since = todayYmd(-1);
      const eps = s.list === 'movies' ? [] : cal.episodes.filter((e) => e.date >= since).map(fromCalEpisode);
      const movies = s.list === 'episodes' ? [] : cal.movies.filter((m) => m.date >= since).map(fromCalMovie);
      const all = s.list === 'all'
        ? [...cal.episodes.filter((e) => e.date >= since).map((e) => ({ d: e.date, it: fromCalEpisode(e) })), ...cal.movies.filter((m) => m.date >= since).map((m) => ({ d: m.date, it: fromCalMovie(m) }))]
          .sort((a, b) => a.d.localeCompare(b.d)).map((x) => x.it)
        : [...eps, ...movies];
      return { kind: 'items', items: all.slice(0, w.limit) };
    }
    case 'sportcenter':
      return { kind: 'sportcenter' };
    case 'tautulli': {
      const r = await tautulliApi.activity().catch(() => null);
      return { kind: 'tautulli', streams: r?.streams ?? [] };
    }
    case 'addon': {
      const r = await addonsApi.catalog(s.addonId, s.catalog);
      return {
        kind: 'items',
        items: r.items.slice(0, w.limit).map((m) => ({
          key: `addon:${s.addonId}:${m.id}`,
          title: m.name,
          sub: [m.type === 'series' ? 'Serie' : m.type === 'movie' ? 'Film' : null, m.releaseInfo].filter(Boolean).join(' · '),
          overview: m.description,
          poster: m.poster ?? undefined,
          backdrop: m.background ?? m.poster ?? undefined,
          ref: null,
          href: `/addon/${s.addonId}/${encodeURIComponent(m.type)}/${encodeURIComponent(m.id)}`,
        })),
      };
    }
    case 'brands': {
      brandsPromise ??= layoutApi.brands().catch((e: unknown) => {
        brandsPromise = null;
        throw e;
      });
      return { kind: 'brands', list: s.list, data: await brandsPromise };
    }
    default: {
      const r = await layoutApi.items(s, w.limit, w.show, undefined, w.filter);
      return { kind: 'items', items: r.items.map(fromExt), heading: r.heading };
    }
  }
}

/** Zelfde volgorde voor iedereen op dezelfde dag/week (zoals de server doet). */
function shuffleRow<T>(list: T[], mode: 'daily' | 'weekly', keyOf: (x: T) => string): T[] {
  const seed = Math.floor(Date.now() / 86_400_000 / (mode === 'weekly' ? 7 : 1));
  const rank = (k: string) => {
    let h = seed ^ 0x9e3779b9;
    for (let i = 0; i < k.length; i++) h = Math.imul(h ^ k.charCodeAt(i), 0x01000193);
    return h >>> 0;
  };
  return [...list].sort((a, b) => rank(keyOf(a)) - rank(keyOf(b)));
}

let brandsPromise: Promise<Brands> | null = null;
const prefsNowSound = () => prefsNow().trailerSound;

/** Logo's van netwerken/studio's en categorietegels; klik opent een bladerpagina. */
function BrandRow({ title, list, data, preview }: { title: string; list: 'networks' | 'studios' | 'categories'; data: Brands; preview: boolean }) {
  if (list === 'categories') {
    if (!data.categories.length) return preview ? <p className="subtle">Geen categorieën (TMDB nodig).</p> : null;
    return (
      <section className="row" aria-label={title}>
        <h2>{title}</h2>
        <div className="row-track" data-focus-group>
          {data.categories.map((c) => (
            <a key={c.id} className="cat-tile" {...linkProps(`/browse/keyword/${c.id}?media=${c.media}`)} style={c.image ? { backgroundImage: `url("${c.image}")` } : undefined}>
              <span>{c.name}</span>
            </a>
          ))}
        </div>
      </section>
    );
  }
  const items = list === 'networks' ? data.networks : data.studios;
  if (!items.length) return preview ? <p className="subtle">Geen logo's (TMDB nodig).</p> : null;
  return (
    <section className="row" aria-label={title}>
      <h2>{title}</h2>
      <div className="row-track brand-track" data-focus-group>
        {items.map((b) => (
          <a key={b.id} className="brand-tile" {...linkProps(`/browse/${list === 'networks' ? 'network' : 'company'}/${b.id}`)} aria-label={b.name} title={b.name}>
            <img src={b.logo} alt="" loading="lazy" />
          </a>
        ))}
      </div>
    </section>
  );
}

/** Laadt een widget één keer per sessie (tot 5 minuten), zodat terug naar Home direct is. */
const widgetCache = new Map<string, { at: number; p: Promise<Loaded> }>();

function useWidget(w: WidgetDef): Loaded | null {
  const { plex, libraries } = useApp();
  const [data, setData] = useState<Loaded | null>(null);
  const key = JSON.stringify([w.source, w.limit, w.show, plex.baseUrl]);
  // Live bibliotheek: Plex heeft iets toegevoegd → opnieuw laden (oude rij blijft staan tot de nieuwe er is)
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    window.addEventListener('tumedia:library-changed', bump);
    return () => window.removeEventListener('tumedia:library-changed', bump);
  }, []);
  useEffect(() => {
    let hit = widgetCache.get(key);
    if (!hit || Date.now() - hit.at > 5 * 60_000) {
      hit = {
        at: Date.now(),
        p: loadWidget(w, plex, libraries).catch((err: unknown) => ({ kind: 'error', message: err instanceof Error ? err.message : 'Laden mislukt' }) as Loaded),
      };
      widgetCache.set(key, hit);
    }
    let live = true;
    void hit.p.then((d) => live && setData(d));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, libraries.length, version]);
  return data;
}

export function clearWidgetCache(): void {
  widgetCache.clear();
}

// ---------- Weergave ----------

/** Kan deze rij als hele lijst geopend worden (meer dan de rij laat zien)? Dan de link erheen. */
export function fullListHref(w: WidgetDef, heading?: string): string | null {
  const s = w.source;
  const ok = ['tmdb', 'mdblist', 'trakt', 'simkl', 'personal', 'addon'].includes(s.kind)
    || (s.kind === 'plex' && ['collection', 'playlist', 'genre', 'unwatched', 'recentlyAdded', 'watchlist'].includes(s.list));
  if (!ok) return null;
  const data = { name: heading ?? w.title, source: s, show: w.show };
  return `/collections/list?src=${encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify(data)))))}`;
}

export function WidgetView({ w, preview = false }: { w: WidgetDef; preview?: boolean }) {
  const data = useWidget(w);
  const [requesting, setRequesting] = useState<ExtItem | null>(null);
  const isHidden = useHiddenCards();
  const { me } = useApp();
  const maxAge = usePolicy(me.profile?.uuid ?? '')?.maxAge ?? null;

  if (!data) {
    return preview ? <p className="subtle">Laden…</p> : <section className="row widget-loading" aria-hidden="true"><h2>{w.title}</h2><div className="row-skeleton" /></section>;
  }
  if (data.kind === 'error') {
    return preview ? <p className="error">{data.message}</p> : null; // op Home: stil overslaan
  }
  if (data.kind === 'brands') return <BrandRow title={w.title} list={data.list} data={data.data} preview={preview} />;
  if (data.kind === 'sportcenter') return <SportWidget w={w} preview={preview} />;
  if (data.kind === 'tautulli') {
    if (!data.streams.length) return preview ? <p className="subtle">Er wordt nu niets afgespeeld op de server.</p> : null;
    return (
      <section className="row" aria-label={w.title}>
        <h2 className="row-title-link"><a {...linkProps('/server')}>{w.title} <span className="row-title-more" aria-hidden="true">›</span></a></h2>
        <div className="row-track" data-focus-group>
          {data.streams.map((s) => (
            <a key={s.key} className="card card-landscape srv-wcard" {...linkProps('/server')}>
              <div className="card-art" style={s.art ?? s.poster ? { backgroundImage: `url("${s.art ?? s.poster}")` } : undefined}>
                <span className="srv-wuser">{s.userThumb ? <img src={s.userThumb} alt="" /> : null}{s.user}</span>
                <span className={`srv-dec srv-wdec ${DECISION[s.decision].cls}`}>{DECISION[s.decision].label}</span>
                <div className="resume-bar srv-wbar"><div style={{ width: `${s.progress}%` }} /></div>
              </div>
              <span className="card-title">{s.title}{s.state === 'paused' ? ' ⏸' : ''}</span>
              <span className="card-sub">{[s.sub, s.player].filter(Boolean).join(' · ')}</span>
            </a>
          ))}
        </div>
      </section>
    );
  }
  if (data.kind === 'nowtv') {
    if (!data.list.length) return preview ? <p className="subtle">Geen zenders met gids gevonden. Markeer zenders als favoriet in Live TV.</p> : null;
    return (
      <section className="row" aria-label={w.title}>
        <h2>{w.title}</h2>
        <div className="row-track" data-focus-group>
          {data.list.map((n) => <NowTvTile key={`${n.ch.accountId}:${n.ch.streamId}`} n={n} />)}
        </div>
      </section>
    );
  }
  if (data.kind === 'leagues') {
    if (!data.list.length) return preview ? <p className="subtle">Geen competities gekozen.</p> : null;
    return (
      <section className="row" aria-label={w.title}>
        <h2>{w.title}</h2>
        <div className="row-track league-track" data-focus-group>
          {data.list.map((s) => <LeagueTile key={s.league.id} s={s} />)}
        </div>
      </section>
    );
  }
  if (data.kind === 'sport') {
    if (!data.events.length) return preview ? <p className="subtle">Nu geen sport in de gids.</p> : null;
    return (
      <section className="row" aria-label={w.title}>
        <h2>{w.title}</h2>
        <div className="row-track" data-focus-group>
          {data.events.map((e) => <SportTile key={e.id} e={e} />)}
        </div>
      </section>
    );
  }
  // Leeftijdsgrens (tiener e.d.): titels uit Plex met een hogere keuring niet op Home
  const items = data.items.filter((it) => !isHidden(it.plex?.ratingKey) && !(maxAge != null && (ratingAge(it.rating) ?? 0) > maxAge));
  if (!items.length) return preview ? <p className="subtle">Geen titels met deze instellingen.</p> : null;
  // Persoonlijke rijen hebben een eigen kop ("Omdat je Reacher keek")
  const heading = data.heading ?? w.title;
  const styleCls = w.style && w.style !== 'default' ? `ws-${w.style}` : '';

  const onOpen = (it: DisplayItem) => {
    if (it.missing) setRequesting(it.missing);
    else if (it.href) navigate(it.href);
  };

  let body: ReactElement;
  const allHref = preview ? null : fullListHref(w, heading);
  const head = allHref ? (
    <h2 className="row-title-link"><a {...linkProps(allHref)}>{heading} <span className="row-title-more" aria-hidden="true">›</span></a></h2>
  ) : <h2>{heading}</h2>;
  const moreTile = allHref ? (
    <a className={`more-tile ${w.view === 'poster' || w.view === 'top10' || w.view === 'netflix' ? 'more-poster' : 'more-landscape'}`} {...linkProps(allHref)} aria-label={`Alles van ${heading} bekijken`}>
      <span className="more-tile-icon" aria-hidden="true">›</span>
      <span>Alles bekijken</span>
    </a>
  ) : null;

  if (w.view === 'spotlight' || w.view === 'billboard') body = <Spotlight items={items} onOpen={onOpen} follow={w.view === 'billboard'} />;
  else if (w.view === 'netflix') body = <NetflixRow heading={heading} head={head} items={items} onOpen={onOpen} moreTile={moreTile} className={styleCls} />;
  else if (w.view === 'wall') {
    body = (
      <section className={`row ${styleCls}`} aria-label={heading}>
        {head}
        <div className="grid wall" data-focus-group>
          {items.map((it) => <Tile key={it.key} it={it} kind="poster" onOpen={onOpen} />)}
        </div>
      </section>
    );
  } else {
    body = (
      <section className={`row ${styleCls}`} aria-label={heading}>
        {head}
        <div className={`row-track ${w.view === 'top10' ? 'top10-track' : ''}`} data-focus-group>
          {items.map((it, i) =>
            w.view === 'top10' ? (
              <Top10Tile key={it.key} it={it} n={i + 1} onOpen={onOpen} />
            ) : (
              <Tile key={it.key} it={it} kind={w.view === 'poster' ? 'poster' : w.view === 'wide' ? 'wide' : 'landscape'} onOpen={onOpen} />
            ),
          )}
          {moreTile}
        </div>
      </section>
    );
  }

  // Beste rijen (Overzicht van de beheerder): tellen welke rij gebruikt wordt om iets te openen
  const countOpen = (e: RMouseEvent) => {
    const el = e.target as HTMLElement;
    if (!preview && el.closest('a[href], .card, .top10') && !el.closest('.row-title-link, .more-tile')) rowOpened(w.title);
  };

  return (
    <>
      <div className="widget-wrap" onClickCapture={countOpen}>{body}</div>
      {requesting && <RequestDialog item={requesting} onClose={() => setRequesting(null)} />}
    </>
  );
}

/** Stand in Seerr van een titel die niet in de bibliotheek staat (uit de widget, of net aangevraagd). */
function missingStatus(it: DisplayItem) {
  if (!it.missing) return undefined;
  return requestStatusOf(it.missing.type, it.missing.tmdbId) ?? it.missing.requestStatus ?? undefined;
}

function CalendarIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true" style={{ verticalAlign: '-1px' }}>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

function BoltIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ verticalAlign: '-1px' }}>
      <path d="M13 2L4 14h7l-1 8 9-12h-7z" />
    </svg>
  );
}

function Badge({ it }: { it: DisplayItem }) {
  useRequestStatuses();
  const { me } = useApp();
  if (it.badge) return <span className={`lib-badge ${it.badgeTone ?? 'req'}`}>{it.badge}</span>;
  // Zonder Plex: geen bibliotheek en geen aanvragen, dus ook geen labels daarover
  if (me.plex === false) return null;
  if (!it.missing && it.key.startsWith('plex:')) return null;
  // Kort (alleen een teken) als er bovenin weinig ruimte is: zie data-mcompact
  if (!it.missing) return <span className="lib-badge own" aria-label="In bibliotheek"><span className="lb-icon" aria-hidden="true">✓</span><span className="lb-text">✓ In bibliotheek</span></span>;
  const l = requestLabels(missingStatus(it), releaseOf(it.missing.type, it.missing.tmdbId) ?? it.missing.releaseDate);
  if (!l) return <span className="lib-badge req" aria-label="Aanvragen"><span className="lb-icon" aria-hidden="true">+</span><span className="lb-text">+ Aanvragen</span></span>;
  return (
    <span className="lib-badge requested" aria-label={[l.main, l.date].filter(Boolean).join(', ')}>
      <span className="lb-icon" aria-hidden="true">{l.icon === 'bolt' ? <BoltIcon /> : <CalendarIcon />}</span>
      <span className="lb-text">{l.main}</span>
      {l.date && <span className="lib-badge-date lb-text">{l.icon === 'bolt' ? <BoltIcon /> : <CalendarIcon />} {l.date}</span>}
    </span>
  );
}

export function Tile({ it, kind, onOpen }: { it: DisplayItem; kind: 'poster' | 'landscape' | 'wide'; onOpen: (it: DisplayItem) => void }) {
  const hover = useHoverPreview(kind === 'landscape' ? it : { ...it, bifUrl: null });
  const ext = useExternalArt(it.key.startsWith('plex:') ? it.ref : null);
  // Brede tegels: achtergrond met clearlogo (Fanart.tv, anders TMDB)
  const wideArt = useArtFrom(kind === 'wide' ? it.ref : null, 'fanart');
  const img = kind === 'poster' ? ext?.poster ?? it.poster : wideArt?.backdrop ?? ext?.backdrop ?? it.backdrop;
  const focusBg = () => {
    if (document.documentElement.dataset.backdrop === 'focus') setFocusBackdrop(wideArt?.backdrop ?? ext?.backdrop ?? it.backdrop);
  };
  const cls = kind === 'poster' ? 'card-poster' : kind === 'wide' ? 'card-wide' : 'card-landscape';
  const menu = useCardMenu(menuRef(it), it.title);
  const content = (
    <>
      <div className="card-art" style={img ? { backgroundImage: `url("${img}")` } : undefined}>
        {kind === 'wide' && wideArt?.logo ? (
          <img className="wide-logo" src={wideArt.logo} alt="" />
        ) : (
          !img && <span className="card-fallback">{it.title}</span>
        )}
        {hover.src && <img className="card-hover-preview" src={hover.src} alt="" />}
        <Badge it={it} />
        {it.flags && <TileFlags flags={it.flags} />}
        <TileScore score={it.score} />
        {it.progress !== undefined && <div className="progress"><div style={{ width: `${Math.round(it.progress * 100)}%` }} /></div>}
        {menu.dots}
      </div>
      <span className="card-title">{it.title}</span>
      {it.sub && <span className="card-sub">{it.sub}</span>}
    </>
  );
  if (it.href && !it.missing) {
    return (
      <a
        className={`card ${cls}`}
        {...linkProps(it.href)}
        onFocus={() => { focusBg(); hover.start(); }}
        onBlur={hover.stop}
        onMouseEnter={() => { focusBg(); hover.start(); }}
        onMouseLeave={hover.stop}
        aria-label={`${it.title}${it.sub ? `, ${it.sub}` : ''}`}
        {...menu.props}
      >
        {content}
      </a>
    );
  }
  return <button className={`card ${cls}`} onClick={() => onOpen(it)} onFocus={focusBg} onMouseEnter={focusBg} aria-label={`${it.title}${it.plex?.upnext ? '' : ', aanvragen'}`} {...menu.props}>{content}</button>;
}

function Top10Tile({ it, n, onOpen }: { it: DisplayItem; n: number; onOpen: (it: DisplayItem) => void }) {
  const ext = useExternalArt(it.key.startsWith('plex:') ? it.ref : null);
  const menu = useCardMenu(menuRef(it), it.title);
  const img = ext?.poster ?? it.poster;
  const focusBg = () => {
    if (document.documentElement.dataset.backdrop === 'focus') setFocusBackdrop(ext?.backdrop ?? it.backdrop);
  };
  const inner = (
    <>
      <span className="top10-n" aria-hidden="true">{n}</span>
      <span className="top10-poster" style={img ? { backgroundImage: `url("${img}")` } : undefined}>
        {!img && <span className="card-fallback">{it.title}</span>}
        <Badge it={it} />
        {it.flags && <TileFlags flags={it.flags} />}
        <TileScore score={it.score} />
        {menu.dots}
      </span>
    </>
  );
  const label = `${n}. ${it.title}${it.missing ? ', aanvragen' : ''}`;
  if (it.href && !it.missing) return <a className="top10" {...linkProps(it.href)} onFocus={focusBg} onMouseEnter={focusBg} aria-label={label} {...menu.props}>{inner}</a>;
  return <button className="top10" onClick={() => onOpen(it)} onFocus={focusBg} onMouseEnter={focusBg} aria-label={label}>{inner}</button>;
}

/** Grote spotlight die wisselt tussen titels (pauzeert als je erin navigeert). */
function Spotlight({ items, onOpen, follow = false }: { items: DisplayItem[]; onOpen: (it: DisplayItem) => void; follow?: boolean }) {
  const noPlex = useApp().me.plex === false;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const root = useRef<HTMLElement>(null);
  // Billboard: volgt de kaart waar je in een Netflix-rij op staat
  const followed = useBillboardItem();
  const it = follow && followed ? followed : items[idx % items.length];
  const meta = useMeta(it.ref);
  const logos = document.documentElement.dataset.logos !== 'off';
  const prefs = usePrefs();
  useRequestStatuses();
  const [trailerOn, setTrailerOn] = useState(false);
  const [sound, setSound] = useState(prefs.trailerSound);
  const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const trailerKey = prefs.spotlightTrailers && !reduced ? meta?.trailer?.key : undefined;

  // Na een paar seconden op dezelfde titel de trailer zonder geluid laten meespelen
  useEffect(() => {
    setTrailerOn(false);
    setSound(prefsNowSound());
    if (!trailerKey) return;
    const t = window.setTimeout(() => setTrailerOn(true), follow ? 2000 : 4500);
    return () => window.clearTimeout(t);
  }, [trailerKey, idx, follow]);

  // Doorschuiven: na 9 s, of na ~50 s als er een trailer speelt (niet als je erin navigeert)
  useEffect(() => {
    if (paused || items.length < 2 || (follow && followed)) return;
    const t = window.setTimeout(() => setIdx((i) => (i + 1) % items.length), trailerOn ? 50_000 : 9000);
    return () => window.clearTimeout(t);
  }, [idx, paused, items.length, trailerOn, follow, followed]);

  // Geluid aan = de trailer opnieuw laden zonder mute. Door de klik mag de browser dan met geluid afspelen.
  const toggleSound = () => setSound((v) => !v);

  const bg = meta?.backdrop ?? it.backdrop;
  return (
    <section
      ref={root}
      className={`hero spotlight${follow ? ' billboard' : ''}`}
      style={bg ? { backgroundImage: `url("${bg}")` } : undefined}
      aria-roledescription="carrousel"
      aria-label="Spotlight"
      onFocus={() => setPaused(true)}
      onBlur={(e) => { if (!root.current?.contains(e.relatedTarget as Node)) setPaused(false); }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {trailerOn && trailerKey && <TrailerFrame ytKey={trailerKey} sound={sound} className="spot-trailer" />}
      {trailerOn && trailerKey && (
        <button className="spot-sound" onClick={toggleSound} aria-pressed={sound} aria-label={sound ? 'Geluid van de trailer uit' : 'Geluid van de trailer aan'}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M11 5L6 9H3v6h3l5 4z" fill="currentColor" />
            {sound ? <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" /> : <path d="M16 9l5 6M21 9l-5 6" />}
          </svg>
          <span>{sound ? 'Geluid uit' : 'Geluid aan'}</span>
        </button>
      )}
      <div className="hero-body" key={it.key}>
        <div className="eyebrow">{it.missing ? (noPlex ? 'Populair' : 'Populair · nog niet in je bibliotheek') : follow ? (followed ? 'Geselecteerd' : 'Uitgelicht') : 'Spotlight'}</div>
        <TitleLogo logo={logos ? meta?.logo : null} title={it.title} />
        {follow ? <InfoLine it={it} meta={meta} /> : <div className="badges"><span className="muted">{it.sub}</span></div>}
        <RatingsRow ratings={meta?.ratings} />
        {(it.overview ?? meta?.overview) && <p className="clamp-2">{it.overview ?? meta?.overview}</p>}
        <div className="hero-actions" data-focus-group>
          {it.missing ? (
            <button className="btn btn-primary" data-autofocus onClick={() => onOpen(it)}>
              {(() => {
                if (noPlex) return 'Bekijken';
                const l = requestLabels(missingStatus(it), it.missing ? releaseOf(it.missing.type, it.missing.tmdbId) ?? it.missing.releaseDate : null);
                return l ? `${l.main}${l.date ? ` · ${l.date}` : ''}` : '+ Aanvragen';
              })()}
            </button>
          ) : (
            <>
              {it.playHref && <button className="btn btn-primary" data-autofocus onClick={() => navigate(it.playHref!)}><PlayIcon /> {it.progress ? 'Hervatten' : 'Afspelen'}</button>}
              {it.href && <a className={`btn ${it.playHref ? '' : 'btn-primary'}`} {...linkProps(it.href)}>Meer info</a>}
            </>
          )}
          {items.length > 1 && !(follow && followed) && (
            <div className="spot-dots" role="tablist" aria-label="Spotlight-titels">
              {items.map((x, i) => (
                <button key={x.key} role="tab" aria-selected={i === idx % items.length} aria-label={x.title} className="spot-dot" onClick={() => setIdx(i)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/** Titels die nog niet in je bibliotheek staan (of alleen via addons): de volledige titelpagina, geen pop-up meer. */
const extStash = new Map<string, ExtItem>();

export function titleHref(item: Pick<ExtItem, 'type' | 'tmdbId' | 'season' | 'episode' | 'epTitle'>): string {
  const q = new URLSearchParams();
  if (item.season !== undefined && item.episode !== undefined) {
    q.set('s', String(item.season));
    q.set('e', String(item.episode));
    if (item.epTitle) q.set('ept', item.epTitle);
  }
  const qs = q.toString();
  return `/title/${item.type}/${item.tmdbId}${qs ? `?${qs}` : ''}`;
}

export function openTitle(item: ExtItem): void {
  extStash.set(`${item.type}:${item.tmdbId}`, item);
  navigate(titleHref(item));
}

/** Vroeger een pop-up; nu gaan we meteen naar de titelpagina. Zo hoeven alle plekken die hem openen niet te veranderen. */
export function RequestDialog({ item, onClose }: { item: ExtItem; onClose: () => void }) {
  useEffect(() => {
    openTitle(item);
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

export function ExtTitlePage({ type, tmdbId, params }: { type: 'movie' | 'tv'; tmdbId: number; params: URLSearchParams }) {
  const { me, plex } = useApp();
  const noPlex = me.plex === false;
  const meta = useMeta({ type, tmdbId });
  const stashed = extStash.get(`${type}:${tmdbId}`);
  const [canRequest, setCanRequest] = useState<boolean | null>(null);
  const [configured, setConfigured] = useState(true);
  const [inPlex, setInPlex] = useState<PlexItem | null>(null);
  const [showTrailer, setShowTrailer] = useState(false);
  useEffect(() => {
    seerrApi.status().then((x) => { setCanRequest(x.canRequest); setConfigured(x.configured); }).catch(() => setConfigured(false));
  }, []);
  useEffect(() => {
    if (noPlex) return;
    plex.findByGuid(`tmdb://${tmdbId}`, type === 'movie' ? 1 : 2).then(setInPlex).catch(() => undefined);
  }, [plex, noPlex, type, tmdbId]);

  const s = Number(params.get('s'));
  const e = Number(params.get('e'));
  const item: ExtItem | null = meta || stashed ? {
    ...(stashed ?? { ratingKey: null }),
    type,
    tmdbId,
    title: meta?.title ?? stashed?.title ?? '',
    year: meta?.year ?? stashed?.year ?? null,
    overview: meta?.overview ?? stashed?.overview ?? null,
    poster: meta?.poster ?? stashed?.poster ?? null,
    backdrop: meta?.backdrop ?? stashed?.backdrop ?? null,
    imdb: stashed?.imdb ?? meta?.imdbId ?? null,
    ...(params.has('s') && params.has('e') ? { season: s, episode: e, epTitle: params.get('ept') ?? undefined } : {}),
  } : null;
  if (!item) return <main className="page" aria-busy="true" />;

  const logos = document.documentElement.dataset.logos !== 'off';
  const line = [type === 'tv' ? 'Serie' : 'Film', item.year, meta?.runtime ? `${meta.runtime} min` : null, meta?.certification, meta?.genres?.slice(0, 3).join(', ')].filter(Boolean).join(' · ');
  return (
    <main className="ext-title">
      <section className="hero hero-detail" style={item.backdrop ? { backgroundImage: `url("${item.backdrop}")` } : undefined}>
        <div className="hero-body">
          <TitleLogo logo={logos ? meta?.logo : null} title={item.title} />
          {meta?.tagline && <div className="tagline">{meta.tagline}</div>}
          <div className="badges"><span className="muted">{line}{!noPlex && !inPlex ? ' · niet in je bibliotheek' : ''}</span></div>
          <RatingsRow ratings={meta?.ratings} />
          {item.overview && <p>{item.overview}</p>}
          <div className="hero-actions" data-focus-group>
            {inPlex && <a className="btn btn-primary" data-autofocus {...linkProps(`/item/${inPlex.ratingKey}`)}><PlayIcon /> In je bibliotheek</a>}
            {meta?.trailer && <button className="btn" onClick={() => setShowTrailer(true)}>Trailer</button>}
            <LocalListButton type={item.type} tmdbId={item.tmdbId} title={item.title} year={item.year} />
            <RecommendButton tmdb={{ type, tmdbId, title: item.title, year: item.year, poster: item.poster, certification: meta?.certification ?? null }} />
          </div>
        </div>
      </section>
      <div className="ext-title-body">
        {!inPlex && (
          <div className="ext-request">
            <div style={{ width: 160, flex: 'none' }}>
              <RequestCard
                m={{ tmdbId: item.tmdbId, mediaType: item.type, title: item.title, year: item.year, poster: item.poster, backdrop: item.backdrop, overview: item.overview ?? '', rating: null, status: noPlex ? 'none' : requestStatusOf(item.type, item.tmdbId) ?? item.requestStatus ?? 'none' }}
                canRequest={!!canRequest && configured}
              />
            </div>
            {!configured && !noPlex && <p className="subtle">Koppel Seerr bij Instellingen om titels aan te vragen.</p>}
          </div>
        )}
        {type === 'movie' && meta?.collection && meta.collection.parts.length > 1 && (
          <CollectionRequest name={meta.collection.name} parts={meta.collection.parts} current={tmdbId} />
        )}
        <ExtSources item={item} />
      </div>
      {showTrailer && meta?.trailer && <TrailerModal youtubeKey={meta.trailer.key} title={item.title} onClose={() => setShowTrailer(false)} />}
    </main>
  );
}

/** Film uit een reeks: alle delen in één keer aanvragen (wat je al hebt of al aanvroeg slaan we over). */
export function CollectionRequest({ name, parts, current, className }: { name: string; parts: MiniItem[]; current: number; className?: string }) {
  const { plex, me } = useApp();
  useRequestStatuses();
  const [have, setHave] = useState<Map<number, string> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [canRequest, setCanRequest] = useState(false);
  useEffect(() => {
    seerrApi.status().then((x) => setCanRequest(x.canRequest && x.configured)).catch(() => setCanRequest(false));
  }, []);
  useEffect(() => {
    if (me.plex === false) { setHave(new Map()); return; }
    let alive = true;
    Promise.all(parts.map((p) => plex.findByGuid(`tmdb://${p.tmdbId}`, 1).then((x) => (x ? [p.tmdbId, x.ratingKey] as [number, string] : null)).catch(() => null)))
      .then((hits) => alive && setHave(new Map(hits.filter((x): x is [number, string] => x !== null))));
    return () => { alive = false; };
  }, [plex, me.plex, parts]);

  const state = (p: MiniItem): 'have' | 'requested' | 'missing' => {
    if (have?.has(p.tmdbId)) return 'have';
    const s = requestStatusOf('movie', p.tmdbId);
    return s && s !== 'none' ? 'requested' : 'missing';
  };
  const missing = parts.filter((p) => state(p) === 'missing');
  const LABEL = { have: 'In je bibliotheek', requested: 'Aangevraagd', missing: 'Ontbreekt' };

  const requestAll = async () => {
    let ok = 0;
    for (const [i, p] of missing.entries()) {
      setBusy(`Aanvragen… ${i + 1} van ${missing.length}`);
      try {
        await seerrApi.request('movie', p.tmdbId, undefined, { title: p.title, poster: p.poster });
        setRequestStatus('movie', p.tmdbId, 'requested');
        ok++;
      } catch {
        // de rest proberen we wel
      }
    }
    setBusy(ok === missing.length ? `✓ ${ok} aangevraagd` : `${ok} van ${missing.length} aangevraagd`);
  };

  return (
    <section className={`coll-req ${className ?? ''}`} aria-label={`Reeks: ${name}`}>
      <div className="coll-req-head">
        <div>
          <span className="subtle">Deze film hoort bij een reeks</span>
          <h3>{name} · {parts.length} films</h3>
          {have && <span className="subtle">{parts.length - missing.length} van {parts.length} in je bibliotheek of aangevraagd</span>}
        </div>
        {have && canRequest && (missing.length > 0 || busy) && (
          <button className="btn btn-primary" disabled={!!busy} onClick={() => void requestAll()}>
            {busy ?? (missing.length === parts.length ? `Hele reeks aanvragen (${missing.length})` : `Ontbrekende aanvragen (${missing.length})`)}
          </button>
        )}
      </div>
      <div className="coll-req-parts">
        {parts.map((p, i) => {
          const st = have ? state(p) : null;
          return (
            <a key={p.tmdbId} className={`coll-part${p.tmdbId === current ? ' is-current' : ''}`} {...linkProps(have?.get(p.tmdbId) ? `/item/${have.get(p.tmdbId)}` : titleHref({ type: 'movie', tmdbId: p.tmdbId }))}>
              {p.poster ? <img src={p.poster} alt="" loading="lazy" /> : <span className="swipe-noart">{p.title}</span>}
              <b>{i + 1}. {p.title}</b>
              <span className="subtle">{p.year ?? 'Binnenkort'}</span>
              {st && <span className={`coll-state is-${st}`}>{LABEL[st]}</span>}
            </a>
          );
        })}
      </div>
    </section>
  );
}

/** Niet in je bibliotheek: bronnen uit je addons (film, of een aflevering: uit Verder kijken of zelf gekozen). */
function ExtSources({ item }: { item: ExtItem }) {
  const addons = useAddons();
  const meta = useMeta(item.imdb ? null : { type: item.type, tmdbId: item.tmdbId });
  const imdb = item.imdb ?? meta?.imdbId ?? null;
  const has = !!addons?.allowed && addons.sourceOrder !== 'plex-only' && (!!addons.iptvVod || addons.addons.some((a) => a.kind === 'video' && a.streams && a.resources.includes('stream')));
  const fixed = item.type === 'tv' && item.season !== undefined && item.episode !== undefined;
  const [pick, setPick] = useState<{ season: number; episode: number } | null>(null);
  if (!has || !imdb) return null;
  if (item.type === 'tv' && !fixed) return <SeriesSources item={item} imdb={imdb} pick={pick} onPick={setPick} />;
  const season = fixed ? item.season! : undefined;
  const episode = fixed ? item.episode! : undefined;
  return <SourceList item={item} imdb={imdb} season={season} episode={episode} epTitle={item.epTitle} />;
}

function SourceList({ item, imdb, season, episode, epTitle }: { item: ExtItem; imdb: string; season?: number; episode?: number; epTitle?: string | null }) {
  const ep = season !== undefined && episode !== undefined;
  const id = ep ? `${imdb}:${season}:${episode}` : imdb;
  return (
    <div className="request-sources">
      <h3>{ep ? `Bronnen voor S${season} E${episode}${epTitle ? ` · ${epTitle}` : ''}` : 'Bronnen uit je addons'}</h3>
      <StreamList
        key={id}
        type={ep ? 'series' : 'movie'}
        id={id}
        title={ep ? `${item.title} · ${epTitle ?? `S${season} E${episode}`}` : item.title}
        ctx={{ id, type: ep ? 'series' : 'movie', metaId: imdb, name: item.title, epTitle: epTitle ?? undefined, season, episode, poster: item.poster ?? undefined, background: item.backdrop ?? undefined, tmdbId: item.tmdbId }}
      />
    </div>
  );
}

type EpList = { seasons: { season: number; episodes: { episode: number; title: string; aired: boolean }[] }[] };
const epLists = new Map<number, Promise<EpList>>();

/** Serie: eerst seizoen en aflevering kiezen, dan de bronnen. */
function SeriesSources({ item, imdb, pick, onPick }: { item: ExtItem; imdb: string; pick: { season: number; episode: number } | null; onPick: (p: { season: number; episode: number } | null) => void }) {
  const [data, setData] = useState<EpList | null>(null);
  const [season, setSeason] = useState<number | null>(null);
  useEffect(() => {
    let p = epLists.get(item.tmdbId);
    if (!p) {
      p = call<EpList>(`/api/meta/tv/${item.tmdbId}/episode-ratings`);
      epLists.set(item.tmdbId, p);
      p.catch(() => epLists.delete(item.tmdbId));
    }
    p.then((d) => {
      setData(d);
      setSeason((cur) => cur ?? d.seasons[0]?.season ?? null);
    }).catch(() => setData({ seasons: [] }));
  }, [item.tmdbId]);
  if (!data) return <div className="request-sources"><p className="subtle">Afleveringen laden…</p></div>;
  const s = data.seasons.find((x) => x.season === season);
  if (pick) {
    const e = data.seasons.find((x) => x.season === pick.season)?.episodes.find((x) => x.episode === pick.episode);
    return (
      <>
        <div className="request-sources" style={{ paddingBottom: 0 }}>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => onPick(null)}>‹ Andere aflevering</button>
        </div>
        <SourceList item={item} imdb={imdb} season={pick.season} episode={pick.episode} epTitle={e?.title || null} />
      </>
    );
  }
  return (
    <div className="request-sources">
      <h3>Kies een aflevering</h3>
      {data.seasons.length > 1 && (
        <div className="filterbar" role="tablist" aria-label="Seizoen" data-focus-group style={{ margin: '0 0 8px', padding: 0 }}>
          {data.seasons.map((x) => (
            <button key={x.season} type="button" role="tab" className="chip" aria-selected={x.season === season} aria-pressed={x.season === season} onClick={() => setSeason(x.season)}>Seizoen {x.season}</button>
          ))}
        </div>
      )}
      {!s?.episodes.length && <p className="subtle">Geen afleveringen bekend.</p>}
      <div className="ep-pick" data-focus-group>
        {s?.episodes.map((e) => (
          <button key={e.episode} type="button" className="ep-pick-row" disabled={!e.aired} onClick={() => onPick({ season: s.season, episode: e.episode })}>
            <span className="ep-pick-num">{e.episode}</span>
            <span className="ep-pick-title">{e.title || `Aflevering ${e.episode}`}</span>
            {!e.aired && <span className="subtle">nog niet uit</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Logotegel van een competitie; klik opent de competitiepagina met zenders. */
export function LeagueTile({ s }: { s: LeagueSummary }) {
  const st = statusText(s);
  const img = s.league.badge ?? s.league.logo;
  const [broken, setBroken] = useState(false);
  return (
    <a className={`league-tile tone-${st.tone}`} {...linkProps(`/sport/${s.league.id}`)} aria-label={`${s.league.name}: ${st.text}`}>
      <span className="league-logo" style={{ background: s.league.color }}>
        {img && !broken ? <img src={img} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} /> : <span className="league-abbr">{s.league.abbr}</span>}
      </span>
      <span className="league-name">{s.league.name}</span>
      <span className="league-status">{st.tone === 'live' && <span className="live-dot" aria-hidden="true" />}{st.text}</span>
    </a>
  );
}

// ---------- Nu op tv ----------

interface NowTv { ch: IptvChannel; now: EpgProgramme; next?: EpgProgramme }

const NL_CH = /(^|[\s|┃:[(-])nl([\s|┃:\])-]|$)/i;

/** Favoriete zenders (anders NL-zenders) met wat er nu op is. */
async function loadNowTv(limit: number, channels?: string[], categories?: string[]): Promise<NowTv[]> {
  const r = await iptv.channels();
  const pool = r.channels.filter((c) => !c.eventTitle && c.epgId);
  let pick: IptvChannel[] = [];
  if (channels?.length) {
    // Vaste zenders, in de gekozen volgorde
    const byKey = new Map(pool.map((c) => [`${c.accountId}:${c.streamId}`, c]));
    pick = channels.map((k) => byKey.get(k)).filter((c): c is IptvChannel => !!c);
  } else if (categories?.length) {
    pick = pool.filter((c) => categories.includes(`${c.accountId}:${c.categoryId}`));
  } else {
    pick = pool.filter((c) => c.favorite);
    if (!pick.length) pick = pool.filter((c) => NL_CH.test(c.name) || NL_CH.test(c.categoryName));
    if (!pick.length) pick = pool;
  }
  pick = pick.slice(0, Math.min(40, limit * 2));
  const now = Date.now();
  const byAcc = new Map<string, IptvChannel[]>();
  for (const c of pick) byAcc.set(c.accountId, [...(byAcc.get(c.accountId) ?? []), c]);
  const out: NowTv[] = [];
  for (const [acc, chans] of byAcc) {
    const g = await iptv.epg(acc, chans.map((c) => c.streamId), now - 4 * 3600_000, now + 4 * 3600_000).catch(() => null);
    if (!g) continue;
    for (const c of chans) {
      const progs = (g.programmes[String(c.streamId)] ?? []).sort((a, b) => a.start - b.start);
      const cur = progs.find((p) => p.start <= now && p.stop > now);
      if (cur) out.push({ ch: c, now: cur, next: progs.find((p) => p.start >= cur.stop) });
    }
  }
  return out.slice(0, limit);
}

function NowTvTile({ n }: { n: NowTv }) {
  const left = Math.max(1, Math.round((n.now.stop - Date.now()) / 60_000));
  const pct = Math.min(100, Math.max(0, ((Date.now() - n.now.start) / (n.now.stop - n.now.start)) * 100));
  return (
    <a className="nowtv-tile" {...linkProps(playLink(n.ch.accountId, n.ch.streamId))} aria-label={`${n.now.title} op ${n.ch.name}, nog ${left} minuten`}>
      <span className="nowtv-art">
        <span className="ev-tag live">LIVE</span>
        <span className="nowtv-logo"><ChannelLogo src={n.ch.logo} name={n.ch.name} size={96} /></span>
        <span className="nowtv-left">NOG {left} MIN</span>
        <span className="progress"><span style={{ width: `${Math.round(pct)}%` }} /></span>
      </span>
      <span className="ev-title">{n.now.title}</span>
      <span className="nowtv-sub"><ChannelName name={n.ch.name} />{n.next && <span> · {clock(n.next.start)} {n.next.title}</span>}</span>
    </a>
  );
}
